import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download, GitCommitHorizontal, PackageCheck, Users } from "lucide-react";
import type { AssuranceAssessment, AssuranceLink } from "../../../assurance/types";
import {
  selectDependencies,
  selectHealth,
  selectLicenses,
  selectPackages,
  selectQuality,
  selectCodeSize,
  selectSbom,
  selectScan,
} from "../../../assurance/model";
import { DomainBadge, FindingLink, ViewEvidenceButton } from "../primitives";
import { SeverityBadge, normalizeSeverity } from "../../ui/SeverityBadge";
import { cn } from "../../../lib/utils";
import {
  BarList,
  CaveatNote,
  EmptyPanel,
  KeyValueGrid,
  MetricTile,
  NotMeasured,
  Pill,
  SectionPanel,
  SegmentStrip,
  StatusPill,
  fmtCurrency,
  fmtDateTime,
  fmtNumber,
  fmtPercent,
  statusTone,
} from "../primitives";

// ---------------------------------------------------------------------------
// Supply chain & SBOM
// ---------------------------------------------------------------------------

export function SupplyChainView({
  assessment,
  demo,
  sbomLinks,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  sbomLinks: AssuranceLink[];
}) {
  const dependencies = selectDependencies(assessment);
  const packages = selectPackages(assessment);
  const sbom = selectSbom(assessment);

  const ecosystems = Object.entries(dependencies.ecosystems).sort(
    (a, b) => b[1] - a[1]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <MetricTile label="Components" value={fmtNumber(dependencies.components)} demo={demo} />
        <MetricTile
          label="Direct / transitive"
          value={
            dependencies.direct !== undefined
              ? `${fmtNumber(dependencies.direct)} / ${fmtNumber(dependencies.transitive)}`
              : undefined
          }
          demo={demo}
        />
        <MetricTile
          label="Vulnerable"
          value={fmtNumber(dependencies.vulnerable)}
          tone={dependencies.vulnerable ? "critical" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Outdated"
          value={fmtNumber(dependencies.outdated)}
          tone={dependencies.outdated ? "warning" : "neutral"}
          demo={demo}
        />
        <MetricTile
          label="Fix available"
          value={fmtNumber(dependencies.fixAvailable)}
          tone="success"
          demo={demo}
        />
        <MetricTile
          label="Blocked upgrades"
          value={fmtNumber(dependencies.blockedUpgrades)}
          tone={dependencies.blockedUpgrades ? "warning" : "neutral"}
          demo={demo}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <SectionPanel title="Ecosystems" description="Components by package ecosystem">
          {ecosystems.length ? (
            <BarList
              ariaLabel="Components by ecosystem"
              items={ecosystems.map(([name, count]) => ({
                label: name,
                value: count,
                colorClass: "bg-chart2/80",
              }))}
            />
          ) : (
            <EmptyPanel demo={demo} />
          )}
        </SectionPanel>

        <SectionPanel
          title="SBOM provenance"
          description="Machine-readable inventory bound to the assessed commit"
          actions={
            sbomLinks.length ? (
              <div className="flex flex-wrap gap-1.5">
                {sbomLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary px-2.5 py-1.5 text-xs"
                  >
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    {link.label}
                  </a>
                ))}
              </div>
            ) : undefined
          }
        >
          {sbom ? (
            <>
              <div className="mb-3 flex flex-wrap gap-2">
                {sbom.commitBound ? (
                  <Pill tone="success" icon={<GitCommitHorizontal className="h-3 w-3" aria-hidden="true" />}>
                    Commit-bound
                  </Pill>
                ) : (
                  <Pill tone="warning">Commit binding unverified</Pill>
                )}
                {sbom.format && (
                  <Pill tone="neutral" icon={<PackageCheck className="h-3 w-3" aria-hidden="true" />}>
                    {sbom.format}
                  </Pill>
                )}
              </div>
              <KeyValueGrid
                demo={demo}
                columns={2}
                items={[
                  { label: "Component count", value: fmtNumber(sbom.componentCount) },
                  {
                    label: "Represented in export",
                    value: fmtNumber(sbom.representedComponentCount),
                  },
                  { label: "Generated", value: fmtDateTime(sbom.generatedAt) },
                  { label: "Repository commit", value: sbom.repositoryCommit, mono: true },
                  { label: "Serial number", value: sbom.serialNumber, mono: true },
                  {
                    label: "Tools",
                    value: sbom.tools.length
                      ? sbom.tools.map((tool) => tool.name).filter(Boolean).join(", ")
                      : undefined,
                  },
                ]}
              />
              {sbom.completeness && (
                <p className="mt-3 text-xs leading-5 text-muted">{sbom.completeness}</p>
              )}
            </>
          ) : (
            <EmptyPanel demo={demo} detail="No SBOM has been generated for this assessment." />
          )}
        </SectionPanel>
      </div>

      <SectionPanel
        title="Priority components"
        description="Direct and high-risk packages with upgrade posture"
        flush
      >
        {packages.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Package</th>
                  <th scope="col">Ecosystem</th>
                  <th scope="col">Risk</th>
                  <th scope="col" className="text-right">Advisories</th>
                  <th scope="col">Known CVEs</th>
                  <th scope="col">Fix version</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg, index) => (
                  <tr key={`${pkg.name}@${pkg.version}` || index}>
                    <td className="whitespace-nowrap font-mono text-xs">
                      <span className="font-semibold text-text">{pkg.name}</span>
                      {pkg.version && pkg.version !== "unknown" && (
                        <span className="text-muted">@{pkg.version}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-muted">{pkg.ecosystem || "—"}</td>
                    <td className="whitespace-nowrap">
                      {pkg.risk && pkg.risk.toLowerCase() !== "none" ? (
                        <SeverityBadge severity={normalizeSeverity(pkg.risk)} size="sm" />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums font-semibold">
                      {pkg.vulnerabilities ?? "—"}
                    </td>
                    <td className="max-w-[260px] truncate font-mono text-[11px] text-muted" title={(pkg.cves || []).join(", ")}>
                      {pkg.cves && pkg.cves.length ? pkg.cves.join(", ") : "—"}
                    </td>
                    <td className="whitespace-nowrap font-mono text-xs">
                      {pkg.fixVersion || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OSS licenses
// ---------------------------------------------------------------------------

const LICENSE_FAMILY_COLORS = ["bg-chart1", "bg-chart2", "bg-chart3", "bg-chart4", "bg-chart5"];

export function LicensesView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const licenses = selectLicenses(assessment);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile
          label="Approved"
          value={fmtNumber(licenses.policy.approved)}
          tone="success"
          demo={demo}
        />
        <MetricTile
          label="Review"
          value={fmtNumber(licenses.policy.review)}
          tone={licenses.policy.review ? "warning" : "neutral"}
          demo={demo}
        />
        <MetricTile
          label="Denied"
          value={fmtNumber(licenses.policy.denied)}
          tone={licenses.policy.denied ? "critical" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Unknown"
          value={fmtNumber(licenses.policy.unknown)}
          tone={licenses.policy.unknown ? "warning" : "success"}
          demo={demo}
        />
      </div>

      <SectionPanel
        title="License families"
        description="Component share by license family, with the required action"
      >
        {licenses.summary.length ? (
          <>
            <SegmentStrip
              ariaLabel="Components by license family"
              segments={licenses.summary.map((family, index) => ({
                label: family.family || "Unknown",
                value: family.components ?? 0,
                colorClass: LICENSE_FAMILY_COLORS[index % LICENSE_FAMILY_COLORS.length],
              }))}
            />
            <div className="mt-4 overflow-x-auto">
              <table className="asr-table">
                <thead>
                  <tr>
                    <th scope="col">Family</th>
                    <th scope="col" className="text-right">Components</th>
                    <th scope="col" className="text-right">Share</th>
                    <th scope="col">Required action</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.summary.map((family, index) => (
                    <tr key={family.family || index}>
                      <td className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-2 font-medium text-text">
                          <span
                            className={cn(
                              "h-2.5 w-2.5 rounded-[3px]",
                              LICENSE_FAMILY_COLORS[index % LICENSE_FAMILY_COLORS.length]
                            )}
                            aria-hidden="true"
                          />
                          {family.family}
                        </span>
                      </td>
                      <td className="whitespace-nowrap text-right tabular-nums">
                        {fmtNumber(family.components) ?? "—"}
                      </td>
                      <td className="whitespace-nowrap text-right tabular-nums">
                        {fmtPercent(family.percent) ?? "—"}
                      </td>
                      <td className="max-w-md text-muted">{family.action || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <EmptyPanel demo={demo} />
        )}
      </SectionPanel>

      <SectionPanel
        title="Obligations requiring review"
        description="Copyleft and restricted licenses with an open legal decision"
        flush
      >
        {licenses.obligations.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Component</th>
                  <th scope="col">License</th>
                  <th scope="col">Distribution context</th>
                  <th scope="col">Decision</th>
                </tr>
              </thead>
              <tbody>
                {licenses.obligations.map((obligation, index) => (
                  <tr key={obligation.component || index}>
                    <td className="whitespace-nowrap font-mono text-xs font-semibold text-text">
                      {obligation.component}
                    </td>
                    <td className="whitespace-nowrap">
                      <Pill tone={statusTone(obligation.decision) === "success" ? "neutral" : "warning"}>
                        {obligation.license}
                      </Pill>
                    </td>
                    <td className="max-w-xs text-muted">{obligation.distribution || "—"}</td>
                    <td className="max-w-sm">
                      <StatusPill status={obligation.decision} demo={demo} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel
              demo={demo}
              icon={<PackageCheck className="h-7 w-7 text-success" aria-hidden="true" />}
              title="No obligations to review"
              detail="Every evaluated dependency is permissive and cleared for distribution — no copyleft, restricted, or unresolved licenses were found."
            />
          </div>
        )}
      </SectionPanel>

      <CaveatNote>
        License analysis supports legal review; it is not legal advice. Final
        decisions on restricted licenses belong with counsel.
      </CaveatNote>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cloud & containers
// ---------------------------------------------------------------------------

export function CloudView({
  assessment,
  demo,
  onSelectFindingId,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  onSelectFindingId: (findingId: string) => void;
}) {
  const scan = selectScan(assessment);
  const infraFindings = assessment.findings.filter((finding) =>
    /^(iac|container)$/i.test(finding.domain)
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile label="Container files" value={fmtNumber(scan.containerfiles)} demo={demo} />
        <MetricTile label="IaC files" value={fmtNumber(scan.iacFiles)} demo={demo} />
        <MetricTile
          label="Infra findings"
          value={fmtNumber(infraFindings.length)}
          tone={infraFindings.some((finding) => finding.severity === "critical") ? "critical" : infraFindings.length ? "warning" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Runtime validation"
          value={undefined}
          sub={demo ? "no live environment assessed" : "Runtime state not collected"}
          demo={demo}
        />
      </div>

      <CaveatNote tone="warning">
        Cloud and container conclusions come from repository evidence only
        (Dockerfiles, IaC templates, and manifests). No registry, cluster, or
        runtime environment was accessed{demo ? " in this synthetic assessment" : ""}.
      </CaveatNote>

      <SectionPanel
        title="Infrastructure findings"
        description="IaC and container findings from the normalized inventory"
        flush
      >
        {infraFindings.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Severity</th>
                  <th scope="col">Finding</th>
                  <th scope="col">Domain</th>
                  <th scope="col">Location</th>
                  <th scope="col">Status</th>
                  <th scope="col"><span className="sr-only">Evidence</span></th>
                </tr>
              </thead>
              <tbody>
                {infraFindings.map((finding) => (
                  <tr
                    key={finding.id}
                    className="cursor-pointer"
                    onClick={() => onSelectFindingId(finding.id)}
                  >
                    <td className="whitespace-nowrap">
                      <SeverityBadge severity={finding.severity} size="sm" />
                    </td>
                    <td className="min-w-[260px] max-w-md">
                      <div className="truncate font-medium text-text">{finding.title}</div>
                      <div className="mt-1">
                        <FindingLink
                          id={finding.id}
                          finding={finding}
                          onOpen={(item) => onSelectFindingId(item.id)}
                        />
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <DomainBadge domain={finding.domain} />
                    </td>
                    <td className="max-w-[260px]">
                      {finding.file ? (
                        <span className="block truncate font-mono text-[11.5px] text-text2">
                          {finding.file}
                          {finding.line ? `:${finding.line}` : ""}
                        </span>
                      ) : (
                        <NotMeasured demo={demo} />
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={finding.status} demo={demo} />
                    </td>
                    <td className="text-right">
                      <ViewEvidenceButton
                        compact
                        onClick={() => onSelectFindingId(finding.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} detail="No infrastructure findings in this assessment." />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Code quality & debt
// ---------------------------------------------------------------------------

export function QualityView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const quality = selectQuality(assessment);
  const codeSize = selectCodeSize(assessment);
  const debt = quality.debtEstimate;
  const maxChurn = Math.max(1, ...quality.hotspots.map((hotspot) => hotspot.churn90d ?? 0));
  // Live evidence mode ships finding-concentration hotspots without
  // static-analysis metrics; render the columns that are actually measured.
  const evidenceHotspots =
    quality.hotspots.length > 0 &&
    quality.hotspots.every((hotspot) => hotspot.cyclomatic === undefined) &&
    quality.hotspots.some((hotspot) => hotspot.findingCount !== undefined);
  // When no static-analysis pass produced maintainability/complexity metrics,
  // show the structural signals that ARE measured (from findings + file tree).
  const structural = quality.structural;
  const showStructural = quality.maintainabilityIndex === undefined && Boolean(structural);

  return (
    <div className="space-y-4">
      {showStructural && structural ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <MetricTile label="Open findings" value={fmtNumber(structural.openFindings)} demo={demo} />
          <MetricTile label="Files with findings" value={fmtNumber(structural.filesWithFindings)} demo={demo} />
          <MetricTile
            label="Finding density"
            value={structural.findingDensityPer100Files !== undefined ? fmtNumber(structural.findingDensityPer100Files) : undefined}
            sub="per 100 source files"
            demo={demo}
          />
          <MetricTile
            label="Hotspot concentration"
            value={fmtPercent(structural.hotspotConcentrationPercent)}
            sub="top 5 files"
            tone={(structural.hotspotConcentrationPercent ?? 0) > 50 ? "warning" : "neutral"}
            demo={demo}
          />
          <MetricTile
            label="Test-to-source"
            value={fmtPercent(structural.testToSourceRatioPercent)}
            sub="test files / source files"
            tone={(structural.testToSourceRatioPercent ?? 100) < 10 ? "warning" : "neutral"}
            demo={demo}
          />
          <MetricTile label="Source files" value={fmtNumber(structural.sourceFiles)} demo={demo} />
          {codeSize.codeLines !== undefined && (
            <MetricTile
              label="Lines of code"
              value={fmtNumber(codeSize.codeLines)}
              sub="programming languages, excl. comments"
              demo={demo}
            />
          )}
          {codeSize.per10kLoc !== undefined && (
            <MetricTile
              label="Findings / 10k LOC"
              value={fmtNumber(codeSize.per10kLoc)}
              sub="code-resident findings only"
              demo={demo}
            />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <MetricTile
            label="Maintainability"
            value={fmtNumber(quality.maintainabilityIndex)}
            sub="index / 100"
            tone={
              quality.maintainabilityIndex === undefined
                ? "neutral"
                : quality.maintainabilityIndex >= 70
                  ? "success"
                  : quality.maintainabilityIndex >= 50
                    ? "warning"
                    : "critical"
            }
            demo={demo}
          />
          <MetricTile label="Test coverage" value={fmtPercent(quality.testCoveragePercent)} demo={demo} />
          <MetricTile
            label="Duplication"
            value={fmtPercent(quality.duplicationPercent)}
            tone={(quality.duplicationPercent ?? 0) > 10 ? "warning" : "neutral"}
            demo={demo}
          />
          <MetricTile
            label="Complexity P95"
            value={fmtNumber(quality.cyclomaticComplexityP95)}
            sub={
              quality.cognitiveComplexityP95 !== undefined
                ? `cognitive ${fmtNumber(quality.cognitiveComplexityP95)}`
                : undefined
            }
            demo={demo}
          />
          <MetricTile label="Code smells" value={fmtNumber(quality.codeSmells)} demo={demo} />
          <MetricTile
            label="Debt"
            value={
              quality.estimatedDebtDays !== undefined
                ? `${fmtNumber(quality.estimatedDebtDays)} d`
                : undefined
            }
            sub={fmtPercent(quality.debtRatioPercent) && `${fmtPercent(quality.debtRatioPercent)} ratio`}
            tone={(quality.debtRatioPercent ?? 0) > 10 ? "warning" : "neutral"}
            demo={demo}
          />
        </div>
      )}

      <SectionPanel
        title={evidenceHotspots ? "Evidence hotspots" : "Complexity hotspots"}
        description={
          evidenceHotspots
            ? "Files ranked by persisted scanner-finding concentration"
            : "Files where complexity, churn, and defects concentrate"
        }
        flush
      >
        {evidenceHotspots ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col" className="text-right">Open findings</th>
                  <th scope="col" className="text-right">Critical</th>
                  <th scope="col">Evidence domains</th>
                </tr>
              </thead>
              <tbody>
                {quality.hotspots.map((hotspot, index) => (
                  <tr key={hotspot.file || index}>
                    <td className="max-w-[380px]">
                      <span className="block truncate font-mono text-[11.5px] text-text" title={hotspot.file}>
                        {hotspot.file}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-right font-semibold tabular-nums">
                      {fmtNumber(hotspot.findingCount) ?? "—"}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <span
                        className={cn(
                          "font-semibold tabular-nums",
                          (hotspot.criticalCount ?? 0) > 0 ? "text-critical" : "text-text2"
                        )}
                      >
                        {fmtNumber(hotspot.criticalCount) ?? "0"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-muted">
                      {hotspot.domains.join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {quality.limitation && (
              <div className="border-t border-border/50 px-4 py-3">
                <CaveatNote>{quality.limitation}</CaveatNote>
              </div>
            )}
          </div>
        ) : quality.hotspots.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">File</th>
                  <th scope="col" className="text-right">LOC</th>
                  <th scope="col" className="text-right">Cyclomatic</th>
                  <th scope="col" className="text-right">Cognitive</th>
                  <th scope="col">Churn (90d)</th>
                  <th scope="col" className="text-right">Defects (12m)</th>
                  <th scope="col">Owner</th>
                </tr>
              </thead>
              <tbody>
                {quality.hotspots.map((hotspot, index) => (
                  <tr key={hotspot.file || index}>
                    <td className="max-w-[280px]">
                      <span className="block truncate font-mono text-[11.5px] text-text" title={hotspot.file}>
                        {hotspot.file}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">
                      {fmtNumber(hotspot.loc) ?? "—"}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <span
                        className={cn(
                          "font-semibold tabular-nums",
                          (hotspot.cyclomatic ?? 0) >= 100
                            ? "text-critical"
                            : (hotspot.cyclomatic ?? 0) >= 50
                              ? "text-high"
                              : "text-text"
                        )}
                      >
                        {fmtNumber(hotspot.cyclomatic) ?? "—"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">
                      {fmtNumber(hotspot.cognitive) ?? "—"}
                    </td>
                    <td className="min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-full max-w-[80px] overflow-hidden rounded-full bg-surface2">
                          <div
                            className="h-full rounded-full bg-chart3"
                            style={{
                              width: `${((hotspot.churn90d ?? 0) / maxChurn) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-text2">
                          {fmtNumber(hotspot.churn90d) ?? "—"}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-right tabular-nums">
                      {fmtNumber(hotspot.defects12m) ?? "—"}
                    </td>
                    <td className="whitespace-nowrap text-muted">{hotspot.owner || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>

      {debt && (
        <SectionPanel
          title="Modeled debt economics"
          description={debt.model}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border/70 bg-surface2/40 p-3.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                Remediation cost
              </div>
              <div className="mt-1 text-lg font-bold tabular-nums text-text">
                {fmtCurrency(debt.remediationCostExpected, debt.currency)}
              </div>
              <div className="mt-0.5 text-xs text-muted">
                range {fmtCurrency(debt.remediationCostLow, debt.currency)} – {fmtCurrency(debt.remediationCostHigh, debt.currency)}
              </div>
            </div>
            <div className="rounded-lg border border-border/70 bg-surface2/40 p-3.5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                Rebuild cost (reference)
              </div>
              <div className="mt-1 text-lg font-bold tabular-nums text-text">
                {fmtCurrency(debt.rebuildCostExpected, debt.currency)}
              </div>
              <div className="mt-0.5 text-xs text-muted">
                range {fmtCurrency(debt.rebuildCostLow, debt.currency)} – {fmtCurrency(debt.rebuildCostHigh, debt.currency)}
              </div>
            </div>
          </div>
          {debt.caveat && (
            <div className="mt-3">
              <CaveatNote tone="warning">{debt.caveat}</CaveatNote>
            </div>
          )}
        </SectionPanel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Development health
// ---------------------------------------------------------------------------

export function HealthView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const health = selectHealth(assessment);
  const history = health.monthlyHistory.filter((month) => month.month);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <MetricTile
          label="Commits"
          value={fmtNumber(health.commits)}
          sub={health.periodMonths ? `${health.periodMonths} months` : health.commitsBasis === "all-time" ? "all-time" : undefined}
          demo={demo}
        />
        <MetricTile label="Pull requests" value={fmtNumber(health.pullRequests)} demo={demo} />
        <MetricTile
          label="Lead time"
          value={
            health.medianLeadTimeHours !== undefined
              ? `${fmtNumber(health.medianLeadTimeHours)} h`
              : undefined
          }
          sub={
            health.medianReviewHours !== undefined
              ? `review ${fmtNumber(health.medianReviewHours)} h`
              : undefined
          }
          demo={demo}
        />
        <MetricTile
          label="Change failure"
          value={fmtPercent(health.changeFailureRatePercent)}
          sub={
            health.revertedCommits !== undefined
              ? `${fmtNumber(health.revertedCommits)} reverts`
              : undefined
          }
          tone={(health.changeFailureRatePercent ?? 0) > 15 ? "critical" : (health.changeFailureRatePercent ?? 0) > 10 ? "warning" : "neutral"}
          demo={demo}
        />
        <MetricTile
          label="Bus factor"
          value={fmtNumber(health.busFactor)}
          tone={(health.busFactor ?? Infinity) <= 3 ? "critical" : "neutral"}
          demo={demo}
        />
        <MetricTile
          label="Ownership"
          value={fmtPercent(health.ownershipConcentrationPercent, 0)}
          sub={
            health.activeContributors !== undefined
              ? `${fmtNumber(health.activeContributors)} active contributors`
              : undefined
          }
          tone={(health.ownershipConcentrationPercent ?? 0) > 50 ? "warning" : "neutral"}
          demo={demo}
        />
      </div>

      {/* Explain the not-collected provider metrics once, rather than leaving
          three bare "not collected" tiles unexplained. */}
      {(health.pullRequests === undefined ||
        health.medianLeadTimeHours === undefined ||
        health.changeFailureRatePercent === undefined) && (
        <CaveatNote>
          {health.limitation ||
            "Pull-request, lead-time, and change-failure metrics require a source-control provider connection (e.g. the GitHub App). Commit, contributor, bus-factor, and ownership figures above are measured from the repository's git history."}
        </CaveatNote>
      )}

      {history.length > 1 && (
        <SectionPanel
          title="Delivery cadence"
          description="Commits per month over the analysis window"
        >
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="asrCommits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--chart-1))" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="rgb(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  vertical={false}
                  stroke="rgb(var(--border) / 0.5)"
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: "rgb(var(--muted))" }}
                  tickLine={false}
                  axisLine={{ stroke: "rgb(var(--border))" }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "rgb(var(--muted))" }}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                />
                <Tooltip
                  cursor={{ stroke: "rgb(var(--border))" }}
                  contentStyle={{
                    background: "rgb(var(--surface-1))",
                    border: "1px solid rgb(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "rgb(var(--text))",
                  }}
                  formatter={(value) => [
                    Number(value ?? 0).toLocaleString(),
                    "Commits",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="commits"
                  stroke="rgb(var(--chart-1))"
                  strokeWidth={2}
                  fill="url(#asrCommits)"
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionPanel>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <SectionPanel
          title="Contributors"
          description="Commit share over the analysis window"
          flush
        >
          {health.contributors.length ? (
            (() => {
              // Only show the Areas column when at least one contributor has
              // measured directory areas (provider-fetched repos); otherwise a
              // column of "—" reads as unfinished.
              const showAreas = health.contributors.some((c) => c.areas.length > 0);
              return (
            <div className="overflow-x-auto">
              <table className="asr-table">
                <thead>
                  <tr>
                    <th scope="col">Contributor</th>
                    <th scope="col" className="text-right">Commits</th>
                    <th scope="col">Ownership</th>
                    {showAreas && <th scope="col">Areas</th>}
                  </tr>
                </thead>
                <tbody>
                  {health.contributors.map((contributor, index) => (
                    <tr key={contributor.name || index}>
                      <td className="whitespace-nowrap font-medium text-text">
                        <span className="inline-flex items-center gap-2">
                          <Users className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                          {contributor.name}
                        </span>
                      </td>
                      <td className="whitespace-nowrap text-right tabular-nums">
                        {fmtNumber(contributor.commits) ?? "—"}
                      </td>
                      <td className="min-w-[130px]">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-full max-w-[80px] overflow-hidden rounded-full bg-surface2">
                            <div
                              className="h-full rounded-full bg-chart1"
                              style={{ width: `${Math.min(100, contributor.ownershipPercent ?? 0)}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-text2">
                            {fmtPercent(contributor.ownershipPercent) ?? "—"}
                          </span>
                        </div>
                      </td>
                      {showAreas && (
                        <td className="max-w-[220px] truncate text-muted">
                          {contributor.areas.join(", ") || "—"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              );
            })()
          ) : (
            <div className="p-4">
              <EmptyPanel demo={demo} />
            </div>
          )}
        </SectionPanel>

        <SectionPanel
          title="Fragile ownership areas"
          description="Subsystems that depend on very few people"
        >
          {health.fragileAreas.length ? (
            <ul className="space-y-2.5">
              {health.fragileAreas.map((area, index) => (
                <li
                  key={area.area || index}
                  className="rounded-lg border border-border/70 bg-surface2/40 px-3.5 py-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[13px] font-semibold text-text">{area.area}</span>
                    <StatusPill status={area.risk && `${area.risk} risk`} demo={demo} />
                  </div>
                  <div className="mt-1 text-xs text-muted">
                    {fmtNumber(area.primaryOwners) ?? "—"} primary /{" "}
                    {fmtNumber(area.secondaryOwners) ?? "—"} secondary owners
                  </div>
                </li>
              ))}
            </ul>
          ) : health.contributors.some((contributor) => contributor.areas.length) ? (
            // Ownership WAS measured (contributor areas exist) and no
            // directory depends on fewer than three recent contributors.
            <EmptyPanel
              demo={demo}
              icon={<Users className="h-7 w-7 text-success" aria-hidden="true" />}
              title="No fragile areas identified"
              detail="Every directory in the sampled commit history was recently worked in by three or more contributors."
            />
          ) : (
            <EmptyPanel demo={demo} />
          )}
        </SectionPanel>
      </div>
    </div>
  );
}
